import { Play } from "phosphor-react"
import {
  AttachmentType,
  ShareAttachmentFragment,
  ShareMessageFragment,
} from "~/__generated__/graphql"
import { getMetaContentMaybe } from "~/common/get-meta-content"
import { ChatShareMessage } from "./components/message/chat-share-message"
import {
  HiddenCount,
  MediaTile,
  isMediaGrid,
} from "./components/message/message-attachments/message-attachments"
import { Logo } from "~/landing/components/logo"
import rambleAppIconWordmark from "~/images/brand/ramble-app-icon-wordmark"
import { isRambleBrand } from "~/common/brand"
import { useSearchParams } from "react-router-dom"
import { cn } from "~/common/cn"

export const ShareMessageCardScreen = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const messageData = getMetaContentMaybe("message-data", {
    json: true,
  }) as ShareMessageFragment | null

  const backgroundColor = searchParams.get("bg")
  const rounded = searchParams.get("rounded")
  const brandLogo = isRambleBrand() ? (
    <img src={rambleAppIconWordmark.src} alt="Ramble" className="h-[19px] shrink-0" />
  ) : (
    <Logo className="h-[30px] shrink-0" />
  )

  return (
    <div
      className={cn("relative mx-auto flex w-full flex-col overflow-hidden bg-neutral-100 p-0", {
        "rounded-md": rounded === "true",
      })}
      style={backgroundColor ? { backgroundColor: `#${backgroundColor}` } : undefined}
    >
      {messageData &&
        (isMediaGrid(messageData.attachments) ? (
          <MediaCollage attachments={messageData.attachments} logo={brandLogo} />
        ) : (
          <div className="flex flex-col gap-2 px-6 py-6">
            <ChatShareMessage message={messageData} playable={false} headerRight={brandLogo} />
          </div>
        ))}
    </div>
  )
}

const COLLAGE_TILE_COUNT = 2

// Two tiles side by side fill the 1.91:1 card, so link previews keep them whole on every platform
const MediaCollage = ({
  attachments,
  logo,
}: {
  attachments: ShareAttachmentFragment[]
  logo: JSX.Element
}) => {
  const hiddenCount = attachments.length - COLLAGE_TILE_COUNT

  return (
    <div className="relative grid h-[188px] w-full grid-cols-2 gap-0.5">
      {attachments.slice(0, COLLAGE_TILE_COUNT).map((attachment, index) => (
        <MediaTile key={attachment.id} imageUrl={attachment.imageUrl} width={600}>
          {index === COLLAGE_TILE_COUNT - 1 && hiddenCount > 0 ? (
            <HiddenCount count={hiddenCount} />
          ) : (
            attachment.attachmentType === AttachmentType.Video && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Play size={40} className="text-base-white" weight="fill" aria-label="Video" />
              </div>
            )
          )}
        </MediaTile>
      ))}
      <div className="absolute bottom-4 right-4 flex items-center rounded-full bg-neutral-100 px-3 py-1.5">
        {logo}
      </div>
    </div>
  )
}

export default ShareMessageCardScreen
